import { ReactElement, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import useLocalStorage from "@/Flowsheets/Hooks/useLocalStorage";

import styles from "./ApproveEncounterFlowsheetConfirmationModal.module.scss";

/**
 * The properties for a {@link ApproveEncounterFlowsheetConfirmationModal} component.
 */
interface IApproveEncounterFlowsheetConfirmationModalProps {
    /**
     * A callback invoked when the flowsheet warning is confirmed or do not show again is enabled.
     */
    onConfirm: () => void;

    /**
     * A callback invoked when leaving the flowsheet is cancelled.
     */
    onCancel?: () => void;
}

/**
 * A modal that asks to confirm when a approving the encounter that contains at least one flowsheet.
 * @param props The ApproveEncounterFlowsheetConfirmationModal properties.
 */
function ApproveEncounterFlowsheetConfirmationModal({ onConfirm, onCancel }: Readonly<IApproveEncounterFlowsheetConfirmationModalProps>): ReactElement {
    const [doNotShowAgain, setDoNotShowAgain] = useLocalStorage(`Flowsheets_ApproveEncounterFlowsheetConfirmationModal_DoNotShowAgain-${cacheStore.GetCurrentEmployee().User_id}`, false);

    useEffect(
        () => {
            if (doNotShowAgain)
                onConfirm();
        },
        [doNotShowAgain]
    );

    const { getText } = useTranslation();
    const [isNotShowAgainChecked, setIsNotShowAgainChecked] = useState(false);

    function toggleDoNotShowAgainChecked(): void {
        setIsNotShowAgainChecked(!isNotShowAgainChecked);
    }

    function confirm(): void {
        setDoNotShowAgain(isNotShowAgainChecked);
        onConfirm();
    }

    return (
        // Portal is required to prevent the parent from scrolling when focusing something in the modal
        createPortal(
            <Modal
                title={getText("Flowsheets_WarningLabel")}
                showX
                disableAutoScroll
                footer={(
                    <div className={styles.approveEncounterFlowsheetConfirmationModalFooter}>
                        <CheckboxControl
                            label={getText("Flowsheets_ConfirmApprove_DoNotShowMessage")}
                            checked={isNotShowAgainChecked}
                            onChange={toggleDoNotShowAgainChecked}
                        />
                        <ButtonGroup spacing="sm">
                            <ButtonControl
                                variant="neutral"
                                onClick={() => onCancel?.()}
                                label={getText("Flowsheets_GoBackLabel")}
                            />
                            <ButtonControl
                                variant="brand"
                                onClick={() => confirm()}
                                label={getText("OKLabel")}
                            />
                        </ButtonGroup>
                    </div>
                )}
                onClose={onCancel}
            >
                <div className={styles.messageContainer}>
                    <p>{getText("Flowsheets_ConfirmApprove_FlowsheetWillBeLockedMessage")}</p>
                    <p>{getText("Flowsheets_ConfirmApprove_EnsureAllUpdateAreCompleteMessage")}</p>
                </div>
            </Modal>,
            document.body
        )
    );
}

export { ApproveEncounterFlowsheetConfirmationModal };
